import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";

interface CreatePrescriptionParams {
  patientId: string;
  encounterId: string;
  carePlanId: string;
  medication: {
    coding: {
      code: string;
      system: string;
      display: string;
    }[];
    text: string;
  };
  prescription?: {
    pharmacy?: {
      ncpdpId?: string;
      vendorId?: string;
      vendorSystem?: string;
    };
    dispense?: {
      daysSupply?: number;
      quantity?: number;
      unit?: {
        coding: {
          code: string;
          system: string;
          display: string;
        }[];
        text: string;
      };
      refills?: number;
      effectiveDate?: string;
    };
    sig?: string;
    noteToPharmacy?: string;
    overrideReason?: string;
    supervisingPhysicianId?: string;
    substitutionAllowed?: boolean;
  };
}

interface CreatePrescriptionResponse {
  bundle: object;
}

export const useCreatePrescription = ({
  patientId,
  encounterId,
}: {
  patientId: string;
  encounterId: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<CreatePrescriptionResponse, CreatePrescriptionParams>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Prescriptions.CREATE(
        patientId,
        encounterId,
      ),
      mutationFn: (params) => {
        return axiosInstance.post("/rx/prescriptions/", params);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Prescriptions.DEFAULT(
            patientId,
            encounterId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.SUMMARY({
            patientId,
            encounterId,
          }),
        });
      },
    },
  });
};
